import {
  AppLayout,
  Box,
  Button,
  Container,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  Grid,
  Header,
  Link,
  SpaceBetween
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppNavigationState } from 'src/components/context/AppContextModels';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import { CardConfiguration, adminCards } from './AdminHomePageConfig';
import { AdminSideNavigationV2 } from './AdminSideNavigationV2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AdminConsoleHeader = () => (
  <Header
    description="The Admin console allows users with admin permissions to create and maintain information presented for each app in FinSuite."
    variant="h1"
  >
    {'Admin Console'}
  </Header>
);

const AdminConsoleContainer = ({ container, onNavigate }: { container: CardConfiguration; onNavigate: (path: string) => void }) => {
  const hasEmbeddedHeader = container.Actions.some((action) => action.embedHeader);

  if (hasEmbeddedHeader) {
    // create list of actions that aren't embedHeader
    const actions_not_embed = container.Actions.filter((action) => !action.embedHeader);
    const actions_to_embed = container.Actions.filter((action) => action.embedHeader);
    if (actions_to_embed.length > 1) {
      throw Error('Only one action may have embedHeader set to true');
    }
    return (
      <Container className="admin-console-container-item">
        <Header
          variant="h3"
          actions={
            <SpaceBetween size="m" direction="horizontal">
              {actions_not_embed.map((action, index) => (
                <Button key={index} onClick={() => onNavigate(action.path)} iconName={action.icon}>
                  {action.buttonText}
                </Button>
              ))}
            </SpaceBetween>
          }
        >
          <Link
            fontSize="heading-m"
            href={actions_to_embed[0].path}
            onFollow={(e) => {
              e.preventDefault();
              onNavigate(actions_to_embed[0].path);
            }}
          >
            <FontAwesomeIcon icon={container.Icon} />
            {' ' + container.Header}
          </Link>
        </Header>
        <Box variant="p">{container.Content}</Box>
      </Container>
    );
  }

  return (
    <Container className="admin-console-container-item">
      <Header
        variant="h3"
        actions={
          <SpaceBetween size="m" direction="horizontal">
            {container.Actions.map((action, index) => (
              <Button key={index} onClick={() => onNavigate(action.path)} iconName={action.icon}>
                {action.buttonText}
              </Button>
            ))}
          </SpaceBetween>
        }
      >
        <FontAwesomeIcon icon={container.Icon} />
        {' ' + container.Header}
      </Header>
      <Box variant="p" margin={'xs'}>
        {container.Content}
      </Box>
    </Container>
  );
};

export const AdminHomePage: React.FC = () => {
  const userAuthData = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationStateMessage = location.state as AppNavigationState;

  // Add useEffect to handle navigation state message and clear state after displaying
  useEffect(() => {
    if (navigationStateMessage?.message) {
      displayFlashMessage(navigationStateMessage.message.header, navigationStateMessage.message.content, navigationStateMessage.message.type);
      // Clear the state after displaying the message
      window.history.replaceState({}, document.title);
    }
  }, [navigationStateMessage]);

  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const displayFlashMessage = (header: string, content: string, flashBarType: FlashbarProps.Type) => {
    setFlashbarItems([
      {
        type: flashBarType,
        header: header,
        content: content,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([])
      }
    ]);
  };

  return (
    <AppLayout
      headerSelector="#h"
      contentType="cards"
      ariaLabels={appLayoutAriaLabels}
      toolsHide={true}
      navigation={<AdminSideNavigationV2 />}
      stickyNotifications
      notifications={<Flashbar items={flashbarItems} />}
      content={
        <div className="app-layout-body">
          <ContentLayout>
            <SpaceBetween direction="vertical" size="s">
              <Container header={<AdminConsoleHeader />} />
              <Grid gridDefinition={adminCards.map(() => ({ colspan: { default: 12, s: 6, m: 6 } }))}>
                {adminCards.map((container, index) => (
                  <AdminConsoleContainer key={index} container={container} onNavigate={navigate} />
                ))}
              </Grid>
            </SpaceBetween>
          </ContentLayout>
        </div>
      }
    />
  );
};
